export default {
	ids: ['enclose', 'tunnel', 'fit', 'rounding', 'fold', 'radius', 'bend', 'bend_angle', 'wander', 'bend_roll'],
	glsl: `
// p: the ray's hit (frame coordinates, stage units). x: distance across the bands to the side or outline they travel to, folded; near, mirror, hold, rim: x's terms, x = min(near, mirror + hold) + rim, kept apart so the lattice adds a displacement in stripes' order; l: along them about the centre. span/anchor: the across span and the cell origin on it. ringTurn, ringLen, turnsPerPx: the ring through p (enclose) for a wave round it. n: the surface normal. depth: the hit's distance from the screen plane along the view axis, su (fog). axial, turn, dTurn: the tube's (tunnel > 0): the distance down the axis from the eye, su (TUBE_RADIUS a radius), the turn round the wall and its change per pixel; dDepth: axial's change per pixel off silhouettes.
struct Surf {
	vec2 p;
	float x;
	float near;
	float mirror;
	float hold;
	float rim;
	float l;
	float span;
	float anchor;
	vec2 ringTurn;
	float ringLen;
	float turnsPerPx;
	vec3 n;
	float depth;
	float unit;
	float hit;
	float axial;
	float turn;
	float dTurn;
	float dDepth;
	float cube;
};

// The tube's radius in su: its walls are isometric with the plane, so down the axis a radius is this far too.
const float TUBE_RADIUS = 100.0;

// The fold's lead, the same for every pixel: uploaded by frameUniforms (common.js), which mirrors the bentRing loop it replaces.
uniform float uFoldLead;

float rectTurn(vec2 p, vec2 size) {
	vec2 c = clamp(p, vec2(0.0), size);
	if (all(greaterThan(c, vec2(0.0))) && all(lessThan(c, size))) {
		float m = min(min(c.x, size.x - c.x), min(c.y, size.y - c.y));
		if (m == c.y) c.y = 0.0;
		else if (m == size.x - c.x) c.x = size.x;
		else if (m == size.y - c.y) c.y = size.y;
		else c.x = 0.0;
	}
	float s = c.y == 0.0 ? c.x : c.x == size.x ? size.x + c.y : c.y == size.y ? 2.0 * size.x + size.y - c.x : 2.0 * (size.x + size.y) - c.y;
	return fract(s / max(2.0 * (size.x + size.y), 1e-6));
}

vec2 turnVec(float t) {
	return vec2(cos(TAU * t), sin(TAU * t));
}

float superNorm(vec2 q) {
	float m = max(abs(q.x), abs(q.y));
	float e = 0.5 * uRounding;
	return e < 1e-3 || m < 1e-6 ? m : m * pow(pow(abs(q.x) / m, 1.0 / e) + pow(abs(q.y) / m, 1.0 / e), e);
}

float bentRing(vec2 q, vec2 dir, float far, float th, vec2 ext, float r0) {
	vec2 r = mat2(cos(th), -sin(th), sin(th), cos(th)) * (q - dir * far);
	float simple = r0 + far - superNorm(r);
	vec2 box = ext + far;
	float boxDepth = min(box.x - abs(r.x), box.y - abs(r.y));
	float m0 = min(box.x, box.y);
	float boxRound = m0 - length(max(abs(r) - (box - m0), 0.0));
	return mix(simple, mix(boxDepth, boxRound, uRounding), uFit);
}

// The tunnel's section: a square of half-size 1 with corners rounded by rc (0 square, 1 round). From o inside, the exit along d and the inward normal there.
float sectionExit(vec2 o, vec2 d, float rc, out vec2 n) {
	vec2 sd = vec2(d.x < 0.0 ? -1.0 : 1.0, d.y < 0.0 ? -1.0 : 1.0);
	d = sd * max(abs(d), 1e-6);
	vec2 tt = (sd - o) / d;
	float t = min(tt.x, tt.y);
	n = tt.x < tt.y ? vec2(-sd.x, 0.0) : vec2(0.0, -sd.y);
	vec2 p = o + d * t;
	float k = 1.0 - rc;
	if (rc > 0.0 && abs(p.x) > k && abs(p.y) > k) {
		vec2 oc = o - sign(p) * k;
		float a = dot(d, d);
		float b = dot(oc, d);
		float c = dot(oc, oc) - rc * rc;
		t = (-b + sqrt(max(b * b - a * c, 0.0))) / a;
		n = -normalize(oc + d * t);
	}
	return t;
}

// The hinge (tunnel below 1): the section's walls tilt by hingeTh from the mouth's plane, hingeF radii ahead of the eye, toward the view axis about the mouth's edges, keeping arc length down them, so at depth z the section is inset by (z − hingeF)·cot hingeTh and each rounded corner is a cone round its fixed centre (a frustum; straight, the eye's ray from depth 0). From o in the section at depth z0 along d (dz depth per unit): the exit, the inward section normal n and the hit's inset from the mouth's edge. Tunnel 1 is sectionExit.
float hingeTh;
float hingeF;

float frustumExit(vec2 o, vec2 d, float z0, float dz, float rc, out vec2 n, out float inset) {
	if (uTunnel >= 1.0) {
		float t = sectionExit(o, d, rc, n);
		vec2 p = abs(o + d * t);
		inset = 1.0 - max(p.x, p.y);
		return t;
	}
	float cs = cos(hingeTh);
	float sn = sin(hingeTh);
	// Each face x = ±H(t), H = 1 − (z − F)·cot: the first of the four the ray reaches.
	float h0 = sn - cs * (z0 - hingeF);
	float t = 1e9;
	n = vec2(0.0, -1.0);
	for (int i = 0; i < 4; i++) {
		vec2 ax = i == 0 ? vec2(1.0, 0.0) : i == 1 ? vec2(-1.0, 0.0) : i == 2 ? vec2(0.0, 1.0) : vec2(0.0, -1.0);
		float den = dot(d, ax) * sn + cs * dz;
		if (den <= 1e-9) continue;
		float ti = (h0 - dot(o, ax) * sn) / den;
		if (ti >= 0.0 && ti < t) {
			t = ti;
			n = -ax;
		}
	}
	vec2 p = o + d * t;
	float k = 1.0 - rc;
	float rho = rc - (cs / max(sn, 1e-9)) * (z0 + dz * t - hingeF);
	inset = 1.0 - max(abs(p.x), abs(p.y));
	if (rc > 0.0 && abs(p.x) > k && abs(p.y) > k && rho > 0.0) {
		// The corner's cone, |o + d·t − K| = rc − (z − F)·cot, its root through the exit as sectionExit's at tunnel 1 (in the conjugate form, finite as the cone flattens).
		vec2 w0 = o - sign(p) * k;
		float ds = rc * sn - cs * (z0 - hingeF);
		float A = sn * sn * dot(d, d) - cs * cs * dz * dz;
		float B = -(sn * sn * dot(w0, d) + ds * cs * dz);
		float C = sn * sn * dot(w0, w0) - ds * ds;
		float q = sqrt(max(B * B - A * C, 0.0));
		float den = B - q;
		float tc = abs(den) > 1e-12 ? C / den : (B + q) / A;
		vec2 w = w0 + d * tc;
		t = tc;
		n = -normalize(w);
		inset = rc - length(w);
	}
	return t;
}

// Bend > 0: the tunnel's path, straight pieces TN_PIECE radii long. Joint j starts piece j, counted from the camera's piece 0 (identity frame, start at the origin), and turns it off piece j - 1 by tnJoint(j): curvature bend × TN_CURVE swinging by wander, toward bend_angle round the tunnel (0 the top, 90 the right) turning by bend_roll, both phases the runtime's integrals over the camera's travel (TN_PIECE_SU su a piece; uSpeedDepth its place in piece 0). Neighbours meet on the plane halving their angle, where both cut the same section.
const float TN_PIECE = 2.0;
const float TN_PIECE_SU = TN_PIECE * TUBE_RADIUS;
const float TN_CURVE = 0.08;
const int TN_PIECES = 40;
float tnCam;
// The hit's inset from the mouth's edge (frustumExit's).
float tnInset;
// A voxel step's hit (relief.glsl.js): its cube's side in the smallest's (0 the wall), its centre's depth down the path from the camera (radii) and its face's normal.
float tnCube;
float tnCubeAt;
vec3 tnCubeN;
float vxH;
void vxSetup();
float vxPieceHit(vec3 p, vec3 rd, vec2 o, vec2 d, vec3 e1, vec3 e2, vec3 a, vec3 j0, vec3 j1, vec3 m0, vec3 m1, float pk, float tEnd, out vec3 n, out float size, out float centre);

vec2 tnJoint(float j) {
	float z = (j - tnCam) * TN_PIECE_SU;
	return vec2(uBend * TN_CURVE * TN_PIECE * cos(TAU * (uWander + uWanderRate * z)), radians(uBendAngle) + TAU * (uBendRoll + uBendRollRate * z));
}

// A piece's frame (T down it, e1 right and e2 down the screen) through joint j = (angle, direction): T turns toward n = sin(dir) e1 - cos(dir) e2, clockwise from the top; the across axes keep their part off n, so the square never rolls. (-angle, dir) on the later frame undoes it.
void tnTurn(inout vec3 T, inout vec3 e1, inout vec3 e2, vec2 j) {
	float sp = sin(j.y);
	float cp = -cos(j.y);
	vec3 dn = (cos(j.x) - 1.0) * (sp * e1 + cp * e2) - sin(j.x) * T;
	T = cos(j.x) * T + sin(j.x) * (sp * e1 + cp * e2);
	e1 += sp * dn;
	e2 += cp * dn;
}

// The ray from the camera, which rides a quadratic Bézier through each joint between piece middles, its frame (f) turned linearly through the joint's angle. Each piece is hit directly and the ray walks piece to piece across the mitre planes, carrying the pieces either side (P, C, N: start J, frame, start joint), so a crossing computes one joint. Returns the hit's depth down the path in radii; h: its section point on the piece's across axes, equal on both sides of a joint; n: the section normal in the camera's frame, laid in the piece's frame turned through the joints as the camera's, so lighting has no crease.
float tnWalk(vec2 uvT, out vec2 h, out vec3 n) {
	tnCam = uSpeedDepth / TN_PIECE_SU;
	tnCube = 0.0;
	vec3 cJ = vec3(0.0);
	vec3 cT = vec3(0.0, 0.0, 1.0);
	vec3 c1 = vec3(1.0, 0.0, 0.0);
	vec3 c2 = vec3(0.0, 1.0, 0.0);
	vec2 jC = tnJoint(0.0);
	vec2 jN = tnJoint(1.0);
	vec2 jP = tnJoint(-1.0);
	vec3 nT = cT;
	vec3 n1 = c1;
	vec3 n2 = c2;
	tnTurn(nT, n1, n2, jN);
	vec3 nJ = cJ + TN_PIECE * cT;
	vec3 pT = cT;
	vec3 p1 = c1;
	vec3 p2 = c2;
	tnTurn(pT, p1, p2, vec2(-jC.x, jC.y));
	vec3 pJ = cJ - TN_PIECE * pT;

	vec3 fT = cT;
	vec3 f1 = c1;
	vec3 f2 = c2;
	vec3 ro;
	if (tnCam >= 0.5) {
		float b = tnCam - 0.5;
		ro = mix(mix(cJ + 0.5 * TN_PIECE * cT, nJ, b), mix(nJ, nJ + 0.5 * TN_PIECE * nT, b), b);
		tnTurn(fT, f1, f2, vec2(b * jN.x, jN.y));
	} else {
		float b = tnCam + 0.5;
		ro = mix(mix(cJ - 0.5 * TN_PIECE * pT, cJ, b), mix(cJ, cJ + 0.5 * TN_PIECE * cT, b), b);
		tnTurn(fT, f1, f2, vec2((b - 1.0) * jC.x, jC.y));
	}
	vec3 rd = uvT.x * f1 + uvT.y * f2 + fT;

	float k = 0.0;
	float t = 0.0;
	vec3 p = ro;
	bool hit = false;
	// Only the camera can start past a mitre plane; once placed, the ray crosses onto each plane exactly, where both pieces cut one section.
	bool placed = false;
	vec2 hs = vec2(0.0);
	vec2 ns = vec2(0.0, -1.0);
	for (int i = 0; i < TN_PIECES; i++) {
		vec3 m0 = normalize(pT + cT);
		vec3 m1 = normalize(cT + nT);
		float out0 = dot(p - cJ, m0);
		float out1 = dot(p - nJ, m1);
		bool fwd = !placed && out1 > 0.0;
		if (placed || (!fwd && out0 >= 0.0)) {
			placed = true;
			vec2 o = vec2(dot(p - cJ, c1), dot(p - cJ, c2));
			vec2 d = vec2(dot(rd, c1), dot(rd, c2));
			vec2 sn;
			// Below tunnel 1 each piece a frustum, its section shrinking with depth down its own axis, so a mitre shows a seam growing with bend × cot of the walls' tilt.
			float inset;
			float tw = max(frustumExit(o, d, (k - tnCam) * TN_PIECE + dot(p - cJ, cT), dot(rd, cT), uRounding, sn, inset), 0.0);
			float dn0 = dot(rd, m0);
			float dn1 = dot(rd, m1);
			float t0 = dn0 < 0.0 ? max(-out0 / dn0, 0.0) : 1e9;
			float t1 = dn1 > 0.0 ? max(-out1 / dn1, 0.0) : 1e9;
			if (vxH > 0.0) {
				vec3 cn;
				float size;
				float centre;
				float tc = vxPieceHit(p, rd, o, d, c1, c2, cT, cJ, nJ, m0, m1, k, min(tw, min(t0, t1)), cn, size, centre);
				if (tc < 1e8) {
					hs = o + d * tc;
					tnCube = size;
					tnCubeAt = centre - tnCam * TN_PIECE;
					tnCubeN = cn;
					t += tc;
					hit = true;
					break;
				}
			}
			if (tw <= min(t0, t1)) {
				hs = o + d * tw;
				ns = sn;
				tnInset = inset;
				t += tw;
				hit = true;
				break;
			}
			fwd = t1 < t0;
			float past = min(t0, t1);
			t += past;
			p += rd * past;
		}
		if (fwd) {
			k += 1.0;
			pJ = cJ; pT = cT; p1 = c1; p2 = c2; jP = jC;
			cJ = nJ; cT = nT; c1 = n1; c2 = n2; jC = jN;
			jN = tnJoint(k + 1.0);
			tnTurn(nT, n1, n2, jN);
			nJ = cJ + TN_PIECE * cT;
		} else {
			k -= 1.0;
			nJ = cJ; nT = cT; n1 = c1; n2 = c2; jN = jC;
			cJ = pJ; cT = pT; c1 = p1; c2 = p2; jC = jP;
			jP = tnJoint(k - 1.0);
			tnTurn(pT, p1, p2, vec2(-jC.x, jC.y));
			pJ = cJ - TN_PIECE * pT;
		}
	}
	// The nearest point on the smooth path (the camera's Bézier, frames turned through the joints): one Newton step from the hit's place between its piece's mitre planes (u, 0 at the start). Rings are the path's cross-sections there, and the normal's frame, the turn round the wall and the depth down the path are read from it.
	vec3 ph = ro + rd * t;
	float d0 = dot(ph - cJ, normalize(pT + cT));
	float d1 = dot(nJ - ph, normalize(cT + nT));
	float u = d0 / max(d0 + d1, 1e-6);
	vec3 gT;
	vec3 g1;
	vec3 g2;
	vec3 gc;
	for (int i = 0; i < 2; i++) {
		gT = cT;
		g1 = c1;
		g2 = c2;
		if (u < 0.5) {
			float b = u + 0.5;
			gc = mix(mix(cJ - 0.5 * TN_PIECE * pT, cJ, b), mix(cJ, cJ + 0.5 * TN_PIECE * cT, b), b);
			tnTurn(gT, g1, g2, vec2((u - 0.5) * jC.x, jC.y));
		} else {
			float b = u - 0.5;
			gc = mix(mix(cJ + 0.5 * TN_PIECE * cT, nJ, b), mix(nJ, nJ + 0.5 * TN_PIECE * nT, b), b);
			tnTurn(gT, g1, g2, vec2((u - 0.5) * jN.x, jN.y));
		}
		if (i == 0) u = clamp(u + dot(ph - gc, gT) / TN_PIECE, -0.5, 1.5);
	}
	vec3 nw = ns.x * g1 + ns.y * g2;
	// Tilted walls face back up the path by their tilt.
	if (uTunnel < 1.0) nw = sin(hingeTh) * nw - cos(hingeTh) * gT;
	if (tnCube > 0.0) nw = tnCubeN;
	n = vec3(dot(nw, f1), dot(nw, f2), dot(nw, fT));
	h = vec2(dot(ph - gc, g1), dot(ph - gc, g2));
	return hit ? (k + u - tnCam) * TN_PIECE : 1e4;
}

Surf surface(Ray ray) {
	vec2 extent = uResolution;
	vec2 p = ray.p;
	Surf s;
	s.p = p;
	s.unit = ray.unit;
	// Rows past the plane's horizon still stand in view.
	s.hit = uRows > 0.0 ? 1.0 : ray.hit;
	s.n = vec3(0.0, 0.0, 1.0);
	s.depth = ray.depth;

	float a = radians(uAngle);
	vec2 axis = vec2(cos(a), sin(a));
	vec2 along = vec2(-axis.y, axis.x);

	vec2 sB = axis * extent;
	float loS = min(sB.x, 0.0) + min(sB.y, 0.0);
	float spanS = max(max(sB.x, 0.0) + max(sB.y, 0.0) - loS, 1.0);
	vec2 lB = along * extent;
	float loL = min(lB.x, 0.0) + min(lB.y, 0.0);
	float span = max(max(lB.x, 0.0) + max(lB.y, 0.0) - loL, 1.0);

	float sRaw = dot(p, axis) - loS;
	s.l = dot(p, along) - loL - 0.5 * span;
	float sMirror = spanS - sRaw;

	vec2 q = p - 0.5 * extent;
	float side = max(min(extent.x, extent.y) * 0.5, 1.0);
	float m = max(abs(q.x), abs(q.y));
	vec2 simpleTurn = mix(turnVec(rectTurn(q + m, vec2(2.0 * m))), turnVec(atan(q.y, q.x) / TAU + 0.375), uRounding);

#ifdef SURFACE_EDGE_DIST
	float d = SURFACE_EDGE_DIST;
	float dSpan = max(SURFACE_INRADIUS, 1.0);
	float perimeter = SURFACE_PERIMETER;
	float core = SURFACE_CORE_DIST;
	vec2 outlineTurn = SURFACE_FLOW ? turnVec(vUV.x) : SURFACE_RING_TURN;
#else
	vec2 hi = max(extent, vec2(1e-4)) - p;
	float d = min(min(p.x, hi.x), min(p.y, hi.y));
	float dSpan = side;
	float perimeter = 2.0 * (extent.x + extent.y);
	float core = length(max(abs(q) - 0.5 * max(vec2(extent.x - extent.y, extent.y - extent.x), 0.0), 0.0));
	vec2 outlineTurn = turnVec(rectTurn(p - d, max(extent - 2.0 * d, 0.0)));
#endif
	float outline = mix(d, dSpan - core, uRounding);

	s.ringTurn = mix(simpleTurn, outlineTurn, uFit);
	float ringSpan = mix(side, dSpan, uFit);
	s.ringLen = mix(mix(8.0, TAU, uRounding) * side, perimeter, uFit);
	s.turnsPerPx = 0.5 * (length(dFdx(s.ringTurn)) + length(dFdy(s.ringTurn))) / max(length(s.ringTurn), 1e-6) / TAU;

	float hold = 1.0 - uFold;
	s.rim = 0.0;
	if (uEnclose < 1e-4) {
		s.near = sRaw;
		s.mirror = sMirror;
		s.hold = hold * (spanS + 1.0);
	} else {
		float k = floor(radians(uAngle) / (0.5 * 3.14159265) + 0.5);
		float th = mix(radians(uAngle), k * 0.5 * 3.14159265, uEnclose);
		float far = min(side * (1.0 - uEnclose) / uEnclose, 1e5);
		vec2 home = mod(k, 2.0) > 0.5 ? 0.5 * extent.yx : 0.5 * extent;
		vec2 ext = mix(0.5 * vec2(spanS, span), home, uEnclose);
		float r0 = mix(0.5 * spanS, side, uEnclose);
		s.near = bentRing(q, axis, far, th, ext, r0);
		s.mirror = 1e30;
		s.hold = 0.0;
		if (uFold > 0.0) {
			s.mirror = bentRing(q, -axis, far, th, ext, r0);
			// uFoldLead: the near ring's most over the mirror's at the frame's corners, edge middles and centre.
			s.hold = hold * (uFoldLead + 1.0);
		}
		vec2 hiFrame = extent - p;
		float frameOutline = mix(min(min(p.x, hiFrame.x), min(p.y, hiFrame.y)), side - length(max(abs(q) - 0.5 * max(vec2(extent.x - extent.y, extent.y - extent.x), 0.0), 0.0)), uRounding);
		s.rim = uFit * uEnclose * (outline - frameOutline);
	}
	s.x = min(s.near, s.mirror + s.hold) + s.rim;
	s.span = mix(spanS, ringSpan, uEnclose);
	// Slides from the stripes' centre to the outline, so zoom scales about the middle, then the rim.
	s.anchor = 0.5 * spanS * (1.0 - uEnclose);

	// On the plane the distance in from the outline, su: the hinge's arc at tunnel 0.
	s.axial = s.x / s.unit;
	s.turn = 0.0;
	s.dTurn = 0.0;
	s.dDepth = uRings > 0.0 ? fwidth(s.axial) : 0.0;
	s.cube = 0.0;
	tnCube = 0.0;
	if (uTunnel > 0.0) {
		// The tube is the camera's: its axis the view axis, its mouth on the screen plane, section radius TUBE_RADIUS. A screen point's ray leaves the eye 1/k behind with slope k × screen, image-zoomed (zoom the focal length here, the lattice's scale on the plane); tilt and elevation do not apply. Tube subsets lock perspective 2: the eye one radius behind the mouth. Straight (bend 0), a wall point at section radius r (sectionExit's, rounding square → circle) lies 1/r radii from the eye.
		vec2 uvT = ray.screen * (uPerspective / (200.0 * uStageUnit * uZoom));
		// Below 1 the hinge: the walls tilt tunnel × 90° from the mouth's plane (the screen's, 2 / perspective radii ahead of the eye), so at 0 the hit is the screen point and the rings are the square section's flat ones.
		hingeTh = 1.5707963 * uTunnel;
		hingeF = 2.0 / max(uPerspective, 1e-4);
		vec2 h;
		vec3 n;
		float depth;
		float inset;
		vxSetup();
		// Only the straight round-or-square tube is closed form; the hinge and the cubes walk the pieces, straight or bent, so the walk holds the one frustumExit (each inlined copy is link time).
		if (uBend > 0.0 || vxH > 0.0 || uTunnel < 1.0) {
			depth = tnWalk(uvT, h, n);
			inset = tnInset;
		} else {
			vec2 sn;
			depth = sectionExit(vec2(0.0), uvT, uRounding, sn);
			h = uvT * depth;
			n = vec3(sn, 0.0);
			inset = 0.0;
		}
		depth = max(depth, 1e-4);
		if (uTunnel < 1.0) {
			// The arc down the wall from the mouth's edge (inset across, depth along), plus the mouth's own distance as the walls come upright: a ring keeps its place on the wall and only moves.
			float arc = inset * cos(hingeTh) + (depth - hingeF) * sin(hingeTh);
			s.axial = TUBE_RADIUS * (arc + hingeF * uTunnel);
			s.depth = TUBE_RADIUS * max(depth - hingeF, 0.0);
		} else {
			s.axial = TUBE_RADIUS * depth;
			s.depth = TUBE_RADIUS * max(depth - 1.0, 0.0);
		}
		// A cube is one band: its centre's.
		if (tnCube > 0.0) {
			s.cube = tnCube;
			s.axial = TUBE_RADIUS * (tnCubeAt - hingeF * (1.0 - uTunnel));
		}
		s.n = n;
		s.turn = atan(h.x, h.y) / TAU;
		// The same angle with its seam opposite: the smaller derivative never spans the jump.
		s.dTurn = min(fwidth(s.turn), fwidth(atan(-h.x, -h.y) / TAU));
		// Bent, a nearer piece hides the wall behind it and depth jumps across that edge: capped at what a continuous wall changes per pixel (depth² × the view's step, with room for the bend).
		s.dDepth = fwidth(s.axial);
		if (uBend > 0.0 || vxH > 0.0) s.dDepth = min(s.dDepth, 4.0 * TUBE_RADIUS * depth * depth * length(fwidth(uvT)));
		// The plane camera's horizon fades out as the tube blends in, so a tilted plane never blacks out the tunnel.
		s.hit = uTunnel < 1.0 ? mix(s.hit, 1.0, smoothstep(0.0, 1.0, uTunnel)) : 1.0;
	}
	return s;
}
`,
}
